import { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  CalendarClock,
  FileSearch,
  FileText,
  Landmark,
  LoaderCircle,
  Scale,
  Search,
  SlidersHorizontal,
  Sparkles,
} from "lucide-react";
import { AppLink, FeedbackBanner, PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  catalogLinkState,
  CatalogPagination,
  formatLegalDate,
} from "../components/LegalCatalogUi.jsx";
import {
  buildNormsSearchUrl,
  normsSearchFromUrl,
} from "../lib/legal-catalog-query.js";

const MATCHING_PASSAGE_LIMIT = 10;

export function NormCatalogPage() {
  const api = useApi();
  const { search: routeSearch, navigate } = useRouter();
  const route = useMemo(() => normsSearchFromUrl(routeSearch), [routeSearch]);
  const [form, setForm] = useState(route);
  const [meta, setMeta] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => setForm(route), [route]);

  useEffect(() => {
    let active = true;
    api.getNormsMeta()
      .then((data) => active && setMeta(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.searchNorms({
      query: route.query,
      jurisdiction: route.jurisdiction,
      type: route.type,
      status: route.status,
      asOfDate: route.asOfDate,
      page: route.page,
      passageLimit: MATCHING_PASSAGE_LIMIT,
    }).then((data) => {
      if (!active) return;
      if (data.pagination.page > data.pagination.totalPages) {
        navigate(buildNormsSearchUrl({
          ...route,
          page: data.pagination.totalPages,
        }), { replace: true });
        return;
      }
      setResult(data);
    })
      .catch((requestError) => {
        if (!active) return;
        setResult(null);
        setError(requestError.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [
    api,
    navigate,
    reloadToken,
    route.asOfDate,
    route.jurisdiction,
    route.page,
    route.query,
    route.status,
    route.type,
  ]);

  const submit = (event) => {
    event.preventDefault();
    const query = form.query.trim();
    if (query && query.length < 2) {
      setError("Ingresá al menos 2 caracteres para buscar normas.");
      return;
    }
    const target = buildNormsSearchUrl({ ...form, query, page: 1 });
    if (`/normas${routeSearch}` === target) {
      setReloadToken((current) => current + 1);
      return;
    }
    navigate(target);
  };

  const goToPage = (page) => navigate(buildNormsSearchUrl({ ...route, page }));

  return (
    <div className="page legal-catalog-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Normativa"
        title="Buscar normas"
        description="Navegá la normativa disponible o buscá por número, nombre y contenido. Afiná el resultado por jurisdicción, tipo, estado y fecha de vigencia."
      />

      <section className="legal-catalog-coverage" aria-label="Cobertura normativa">
        <span><Scale size={15} /> Normativa indexada</span>
        <span><Landmark size={15} /> {meta?.jurisdictions?.length || 0} jurisdicciones</span>
        <span><BadgeCheck size={15} /> Versiones y fuentes identificadas</span>
        <strong>{Number(meta?.total || 0).toLocaleString("es-AR")} normas</strong>
      </section>

      <form className="legal-catalog-search has-filters" onSubmit={submit}>
        <label htmlFor="norm-query">Número, nombre o contenido</label>
        <div className="legal-catalog-search-control">
          <Search size={20} />
          <input
            id="norm-query"
            value={form.query}
            onChange={(event) => {
              setForm((current) => ({ ...current, query: event.target.value }));
              setError("");
            }}
            placeholder="Ej. Ley 9.001, Código Civil o responsabilidad objetiva"
            autoFocus
          />
          <button className="primary-button" disabled={loading}>
            {loading ? <LoaderCircle className="spin" size={17} /> : <Search size={17} />}
            {loading ? "Buscando…" : "Buscar"}
          </button>
        </div>
        <div className="legal-catalog-filters">
          <span className="legal-catalog-filter-title"><SlidersHorizontal size={16} /> Afinar catálogo</span>
          <label>
            Jurisdicción
            <select
              value={form.jurisdiction}
              onChange={(event) => setForm((current) => ({
                ...current,
                jurisdiction: event.target.value,
              }))}
            >
              <option value="all">Todas</option>
              {(meta?.jurisdictions || []).map((item) => (
                <option value={item.id} key={item.id}>{item.name} ({item.count})</option>
              ))}
            </select>
          </label>
          <label>
            Tipo
            <select
              value={form.type}
              onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
            >
              <option value="all">Todos</option>
              {(meta?.types || []).map((item) => (
                <option value={item.value} key={item.value}>{item.label} ({item.count})</option>
              ))}
            </select>
          </label>
          <label>
            Estado
            <select
              value={form.status}
              onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="all">Todos</option>
              {(meta?.statuses || []).map((item) => (
                <option value={item.value} key={item.value}>{item.label} ({item.count})</option>
              ))}
            </select>
          </label>
          <label>
            Vigente al
            <input
              type="date"
              value={form.asOfDate}
              onChange={(event) => setForm((current) => ({
                ...current,
                asOfDate: event.target.value,
              }))}
            />
          </label>
        </div>
      </form>

      {error && <FeedbackBanner>{error}</FeedbackBanner>}
      {!loading && result?.warning && <FeedbackBanner>{result.warning}</FeedbackBanner>}

      {loading && (
        <section className="legal-catalog-loading" aria-live="polite">
          <LoaderCircle className="spin" size={22} />
          <span>Consultando la normativa…</span>
        </section>
      )}

      {!loading && result?.items?.length > 0 && (
        <section className="legal-catalog-results" aria-live="polite">
          <header>
            <div>
              <span className="eyebrow">{route.query ? "Resultados relevantes" : "Explorar catálogo"}</span>
              <h2>{result.pagination.total.toLocaleString("es-AR")} norma{result.pagination.total === 1 ? "" : "s"}</h2>
              <p>Una norma por resultado, con prioridad para número y nombre antes que coincidencias sólo en artículos.</p>
            </div>
            {route.query && (
              <span className={`search-mode-pill is-${result.searchMode}`}>
                {result.searchMode === "hybrid" ? <Sparkles size={14} /> : <Search size={14} />}
                {result.searchMode === "hybrid" ? "Búsqueda híbrida" : "Búsqueda textual"}
              </span>
            )}
          </header>

          <div className="legal-catalog-list">
            {result.items.map((item) => (
              <article className="legal-catalog-card" key={item.id}>
                <div className="legal-catalog-card-icon"><Scale size={21} /></div>
                <div className="legal-catalog-card-content">
                  <div className="legal-catalog-card-kicker">
                    <span className="catalog-match-pill is-title">{item.typeLabel}</span>
                    <span className={`norm-status is-${item.status}`}>{item.statusLabel}</span>
                    {item.official && <span className="official-source"><BadgeCheck size={12} /> Fuente oficial</span>}
                    {route.query && (
                      <span className={`catalog-match-pill is-${item.matchType}`}>
                        {["hybrid", "semantic"].includes(item.matchType) && <Sparkles size={11} />}
                        {item.matchReason}
                      </span>
                    )}
                  </div>
                  <AppLink
                    to={item.url}
                    state={catalogLinkState(`/normas${routeSearch}`)}
                    className="legal-catalog-title"
                  >
                    {item.shortName || item.name}
                  </AppLink>
                  {item.shortName && item.shortName !== item.name && (
                    <p className="legal-catalog-subtitle">{item.name}</p>
                  )}
                  <div className="legal-catalog-meta">
                    <span><Landmark size={14} /> {item.jurisdiction.name}</span>
                    {item.number && <span><FileText size={14} /> N.º {item.number}</span>}
                    {item.effectiveFrom && <span><CalendarClock size={14} /> Desde {formatLegalDate(item.effectiveFrom)}</span>}
                  </div>
                  {item.passages?.length > 0 ? (
                    <div className="legal-catalog-passages">
                      {item.passageMatchCount > 1 && (
                        <div className="legal-catalog-passages-heading">
                          <strong>
                            {item.passageMatchCount.toLocaleString("es-AR")} coincidencias en artículos
                          </strong>
                          {item.passageMatchCount > item.passages.length && (
                            <small>Mostrando las {item.passages.length} más relevantes</small>
                          )}
                        </div>
                      )}
                      {item.passages.map((matchedPassage, index) => (
                        <blockquote
                          className="legal-catalog-passage"
                          key={matchedPassage.id || `${item.id}-${index}`}
                        >
                          {matchedPassage.text}
                          {matchedPassage.reference && <small>{matchedPassage.reference}</small>}
                          <AppLink
                            to={matchedPassage.url}
                            state={catalogLinkState(`/normas${routeSearch}`)}
                            className="legal-passage-source"
                          >
                            Abrir fuente Tribunalea
                          </AppLink>
                        </blockquote>
                      ))}
                    </div>
                  ) : item.summary ? (
                    <blockquote className="legal-catalog-passage">
                      {item.summary}
                      <AppLink
                        to={item.url}
                        state={catalogLinkState(`/normas${routeSearch}`)}
                        className="legal-passage-source"
                      >
                        Abrir fuente Tribunalea
                      </AppLink>
                    </blockquote>
                  ) : null}
                  <AppLink
                    to={item.url}
                    state={catalogLinkState(`/normas${routeSearch}`)}
                    className="secondary-button catalog-open-button"
                  >
                    Abrir norma
                  </AppLink>
                </div>
              </article>
            ))}
          </div>
          <CatalogPagination
            pagination={result.pagination}
            onPage={goToPage}
            itemLabel="normas"
            ariaLabel="Páginas del catálogo de normas"
          />
        </section>
      )}

      {!loading && !error && result?.items?.length === 0 && (
        <section className="legal-catalog-empty">
          <FileSearch size={26} />
          <h2>No encontramos normas</h2>
          <p>Probá con el número, un nombre más breve o quitá alguno de los filtros.</p>
        </section>
      )}
    </div>
  );
}
